/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { CardContent } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import { errorToObject, v2 } from "@prftesp/cle-logger";
import { useState } from "react";
import { EmptyPlaceholder } from "../../../components";
import { ActionButtons } from "../../../components/Common";
import PageTabs from "../../../components/Common/PageTabs";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import errorCodes from "../../../utility/errorCodes";
import { updateConfigAsync } from "../services/ConfigService";
import ConfigurationForm from "./ConfigurationForm";
import { ScriptDocs } from "./ScriptDocs";
import { ScriptTags } from "./ScriptTags";
import SurveyForm from "./SurveyForm";

const ConfigurationDetailsCard = ({
	selectedConfig,
	setIsLoading,
	edited,
	contactFlows,
	setEdited,
	selectedConfigId,
	setSelectedConfig,
	validationMessage,
	setValidationMessage,
	setNotification,
	refreshConfigs,
	isCurrentConfigValid,
	updateChatConfig,
	chatConfigs,
}) => {
	const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

	if (!selectedConfig) {
		return <EmptyPlaceholder message="Please select a configuration to view / edit" />;
	}

	const handleRemoveConfig = async () => {
		try {
			setIsLoading(true);
			await updateConfigAsync({
				deleteConfig: {
					configId: selectedConfigId,
					config: selectedConfig,
				},
			});
			setNotification({
				message: "Successfully removed configuration.",
				severity: "success",
			});
			refreshConfigs(true);
		} catch (error) {
			v2.error(`Error deleting web chat config`, {
				additionalData: {
					error: errorToObject(error),
					data: { selectedConfig, selectedConfigId },
				},
				logCode: errorCodes.DELETE_WEBCHAT_CONFIG_ERROR,
				componentName: "COMPONENT_NAME",
			});
			setNotification({
				message: "Failed to remove configuration.",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelConfigChanges = () => {
		setSelectedConfig({
			...chatConfigs.find((config) => config.configId === selectedConfigId).config,
		});
		setEdited(false);
		setValidationMessage(undefined);
	};

	const handleSaveConfigChanges = async () => {
		setIsLoading(true);
		await updateChatConfig(
			{ configId: selectedConfigId, config: selectedConfig },
			"Successfully saved configuration.",
			"Failed to save configuration.",
		);
		setIsLoading(false);
	};

	const handleCloneConfig = async (_event) => {
		if (!edited) {
			let updateConfig = {
				configId: selectedConfigId,
				config: { ...selectedConfig },
			};
			updateConfig.configId = uuidv4();
			updateConfig.config.friendlyName = selectedConfig.friendlyName + " (Copy)";
			await updateChatConfig(
				updateConfig,
				"Successfully duplicated configuration.",
				"Failed to duplicate configuration.",
			);
		}
	};

	const tabsConfig = [
		{
			label: "Web Chat",
			id: "webChatConfiguration",
			component: (
				<ConfigurationForm
					selectedConfiguration={selectedConfig}
					setSelectedConfiguration={(config) => setSelectedConfig(config)}
					setFormEdited={setEdited}
					contactFlows={contactFlows}
					formEdited={edited}
				/>
			),
			default: true,
		},
		{
			label: "Web Chat Survey",
			id: "webChatSurveyConfiguration",
			component: (
				<SurveyForm
					selectedConfiguration={{
						...selectedConfig,
						surveyQuestions: selectedConfig.surveyQuestions || [],
					}}
					setFormEdited={setEdited}
					formEdited={edited}
					setSelectedConfiguration={(config) => {
						setSelectedConfig(config);
					}}
					setValidationMessage={setValidationMessage}
				/>
			),
			default: true,
		},
		{
			label: "HTML Embedding",
			id: "htmlEmbedding",
			component: <ScriptTags selectedConfigId={selectedConfigId} />,
			default: true,
		},
		{
			label: "Dev Documentation",
			id: "developerDocumentation",
			component: <ScriptDocs />,
			default: true,
		},
	];

	return (
		<>
			<CardContent className="d-flex align-items-center justify-content-between flex-wrap">
				<h5 className="font-weight-bold m-0">Edit Configuration</h5>
				<div>
					<ActionButtons
						showRemove
						showCancel={edited}
						showSave={edited}
						onSave={handleSaveConfigChanges}
						onRemove={() => setRemoveConfirmOpen(true)}
						onCancel={handleCancelConfigChanges}
						disableSave={!isCurrentConfigValid || validationMessage !== undefined}
						className="float-right"
					/>
					<span data-test-id="webchat-config-duplicate-btn-wrapper">
						<ActionButtons
							showCancel={!edited && isCurrentConfigValid}
							className="float-right mr-0 mr-lg-2 mt-2 mt-lg-0"
							cancelLabel="Duplicate Configuration"
							onCancel={handleCloneConfig}
						/>
					</span>
				</div>
			</CardContent>
			<div className="d-none" data-test-id="webchat-config-id">
				{selectedConfigId}
			</div>
			<PageTabs tabs={tabsConfig} onTabContentModified={setEdited} requireConfirmOnChange={false} className="" />
			<CardContent className="pt-0 pb-3">
				{validationMessage && <div className={"text-danger mr-2"}>{validationMessage}</div>}
			</CardContent>
			<ConfirmDialog
				title={"Remove configuration?"}
				message={"Are you sure you want to delete the configuration?"}
				confirmOpen={removeConfirmOpen}
				onClose={async (confirmed) => {
					if (confirmed) {
						await handleRemoveConfig();
					}
					setRemoveConfirmOpen(false);
				}}
			/>
		</>
	);
};

export default ConfigurationDetailsCard;
